import { SkyDataGrid, SkyText, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type CSSProperties } from "react";
import { Link, useLocation } from "wouter";
import type { Reliability, ReliabilityWindow } from "@shared/types/reliability";
import type { ScheduleSummary } from "@shared/types/schedules";
import { WatchBadge } from "@/components/notifications/WatchBadge";
import { ReliabilityWindowToggle } from "@/components/reliability/ScheduleReliability";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { useMinuteClock } from "@/hooks/use-minute-clock";
import { absolutePath } from "@/lib/routes";
import { findScheduleClashes } from "@/lib/schedules/clashes";
import { timezoneShortLabel } from "@/lib/timezones";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { PauseScheduleDialog } from "./PauseScheduleDialog";
import {
  ActionsCell,
  LastRunCell,
  NextRunCell,
  RunsCell,
  StateCell,
  type ActionsContext,
} from "./SchedulesTableCells";
import { useResumeSchedule } from "./useSchedulePause";
import { formatInstant, zoneCaption, zoneLabel } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { destinationBlockReason, useDestinationReadiness } from "@/hooks/use-destination-readiness";
import { BREAKABLE_NAME_CLASS, breakableName } from "@/lib/breakableName";

/**
 * The schedules list (FR-50).
 *
 * `next_fire_at` is the column that earns the screen: it is the only place a
 * user can check that the schedule they wrote means what they meant. A disabled
 * schedule shows no next run at all rather than a stale one, because the runner
 * stops advancing the column when a schedule is disabled and a time left on
 * screen would read as a promise the system is not making.
 *
 * Session 10 (P9): a paused schedule carries a "Paused until Sep 30" badge beside its state, and
 * its row menu offers Resume instead of Pause…; the Pause dialog and the resume call live here, so
 * the list page needs no new wiring. An enabled schedule whose skip dates leave it no firing
 * inside two years reads "No upcoming run" and stays enabled.
 */

/**
 * Round 3 (owner, 2026-09-30): every column was a percentage, and the "Paused until" badge ran out
 * of the State column into Next run. Widths measured in Chromium in both fonts (client/AGENTS.md),
 * border-box with the cell's 24 px padding: State holds that badge (107 px at its widest day
 * start, "Sep 30") and Actions its header (99 px); the rest share what is left in proportion to a
 * minimum that holds their header (Schedule with its sort icon) and a next or last run on two
 * lines at the comma ("Sep 29, 2026," 98 px at 14 px). The floor, 968 px, fits the pane at 1280 px
 * with the drawer expanded; a narrower pane scrolls the grid under a pinned Actions column.
 *
 * #1082 (QA, 10/14: at 1024 px Last run sat under Actions and a Contributor lost part of Next run):
 * 968 px was wider than the 927 px pane at 1024 px (`PANE_WIDTH.at1024`). The floor is now 920 px:
 * State holds the badge (132: 108 plus padding), Actions the eye and the menu (100: its header 99),
 * Schedule its sortable header (144, a long name wraps beside its Watch badge) and Runs 136 (the
 * description wraps). Time zone, Next run and Last run keep 136, which holds the two-line times and
 * the success rate. Every column is in view beside Actions at 1024 px (the nav rail) and from 1229 px with the drawer
 * open (floor plus 309). Between them Last run, and Next run below about 1130 px, scroll under
 * Actions.
 */
const STATE_WIDTH = 132;
const ACTIONS_WIDTH = 100;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  name: 144,
  description: 136,
  timezone: 136,
  nextFireAt: 136,
  lastFiredAt: 136,
};

export interface SchedulesTableProps {
  schedules: ScheduleSummary[];
  isLoading: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canStartRun: boolean;
  /** `runs.read`: the row's "View run history" eye (round 4 item 6). */
  canReadRuns: boolean;
  onRunNow: (schedule: ScheduleSummary) => void;
  onDelete: (schedule: ScheduleSummary) => void;
  /** Enable or disable from the row (the editor's switch was the only way, and hard to find). */
  onSetEnabled: (schedule: ScheduleSummary, enabled: boolean) => void;
  /**
   * #828 (Level B): each schedule's success rate and last outcome, under its Last run, and the
   * 7 d or 30 d choice above the table. Absent: neither is shown (a caller without `runs.read`).
   */
  reliability?: {
    data: Reliability | undefined;
    window: ReliabilityWindow;
    onWindowChange: (window: ReliabilityWindow) => void;
  };
}

export function SchedulesTable({
  schedules,
  isLoading,
  canWrite,
  canDelete,
  canStartRun,
  canReadRuns,
  onRunNow,
  onDelete,
  onSetEnabled,
  reliability,
}: SchedulesTableProps) {
  const [, navigate] = useLocation();
  const zone = useDisplayTz();
  // A pause ends on its own, so the State badge and the row menu re-read the clock each minute
  // (`useMinuteClock`).
  const now = useMinuteClock();
  const notice = useNotice();
  const showApiError = useApiErrorNotice();
  const resume = useResumeSchedule();
  const [pauseTarget, setPauseTarget] = useState<ScheduleSummary | null>(null);

  const resumeSchedule = async (schedule: ScheduleSummary): Promise<void> => {
    try {
      const saved = await resume.mutateAsync(schedule.id);
      notice.success(
        saved.nextFireAt
          ? `${saved.name} is resumed. Next import ${formatInstant(saved.nextFireAt, zone, "short", { named: true })}.`
          : `${saved.name} is resumed.`,
      );
    } catch (error) {
      showApiError(error, "The schedule could not be resumed.");
    }
  };

  // Round 9 (B-28): enabled schedules that share a connection and a firing minute, by schedule id.
  const clashNames = useMemo(() => {
    const byId = new Map<string, string[]>();
    for (const schedule of schedules) {
      const clashes = findScheduleClashes(schedule, schedules);
      if (clashes.length > 0)
        byId.set(
          schedule.id,
          clashes.map((clash) => clash.other.name),
        );
    }
    return byId;
  }, [schedules]);

  const pinnedEdge = usePinnedColumnEdge<HTMLElement>(true);
  // #375, #923: why Run now is off while the destination is not Ready.
  const destinationReason = destinationBlockReason(useDestinationReadiness());
  const actionsContext: ActionsContext = {
    canWrite,
    canDelete,
    canStartRun,
    canReadRuns,
    destinationReason,
    now,
    navigate,
    onRunNow,
    onDelete,
    onSetEnabled,
    onResume: (schedule) => void resumeSchedule(schedule),
    onPause: setPauseTarget,
  };
  const columns: SkyDataGridColumn<ScheduleSummary>[] = [
    {
      key: "name",
      header: "Schedule",
      width: "26%",
      sortable: true,
      // Round 10 (C4): the name opens the editor; the badge beside it is the caller's own Watch,
      // its level in words and changed in place, as on the Connections grid.
      renderCell: (_value, row) => (
        <span className="flex flex-wrap items-center gap-2">
          <Link
            href={absolutePath(`/schedules/${row.id}`)}
            title={row.name}
            className={`min-w-0 underline-offset-2 hover:underline ${BREAKABLE_NAME_CLASS}`}
          >
            <SkyText size={300} weight="semibold" color="accent">
              {breakableName(row.name)}
            </SkyText>
          </Link>
          <WatchBadge
            compact
            targetType="schedule"
            targetId={row.id}
            name={row.name}
            subject={row.name}
            testIds={{ watch: `schedule-watch-${row.id}`, watching: `schedule-watching-${row.id}` }}
          />
        </span>
      ),
    },
    {
      key: "description",
      header: "Runs",
      width: "24%",
      // Walk 2026-10-02, D6: the times here are the schedule's own, so they carry its zone's
      // abbreviation ("At 02:00 AM PDT"); Next run and Last run are the viewer's, said in the caption.
      renderCell: (_value, row) => (
        <RunsCell row={row} now={now} clashWith={clashNames.get(row.id)} />
      ),
    },
    {
      key: "timezone",
      header: "Time zone",
      width: "14%",
      // Round 6, W8: the zone the Runs column is in, with no clock. The current time there read
      // like a run time beside Next run; the live clock stays only in the editor's zone picker.
      renderCell: (_value, row) => (
        <SkyText size={300} block testId={`schedule-zone-${row.id}`}>
          {timezoneShortLabel(row.timezone, now)}
        </SkyText>
      ),
    },
    {
      key: "enabled",
      header: "State",
      width: `${STATE_WIDTH}px`,
      renderCell: (_value, row) => <StateCell row={row} zone={zone} now={now} />,
    },
    {
      key: "nextFireAt",
      header: "Next run",
      width: "16%",
      renderCell: (_value, row) => <NextRunCell row={row} zone={zone} />,
    },
    {
      key: "lastFiredAt",
      header: "Last run",
      width: "16%",
      renderCell: (_value, row) => <LastRunCell row={row} zone={zone} reliability={reliability} />,
    },
    {
      key: "actions",
      header: "Actions",
      width: `${ACTIONS_WIDTH}px`,
      align: "right",
      renderCell: (_value, row) => <ActionsCell row={row} ctx={actionsContext} />,
    },
  ];

  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  return (
    // The grid is wrapped in a labelled region rather than given an aria-label
    // of its own: `SkyDataGridProps` does not forward arbitrary attributes, so
    // an `aria-label` passed to it would be dropped silently. The zone belongs
    // in the label because a next-run time without one misleads every time.
    <section
      ref={pinnedEdge}
      aria-label={`Schedules. ${zoneCaption(zone)}.`}
      style={{ "--schedules-grid-floor": `${layout.floor}px` } as CSSProperties}
    >
      {reliability && schedules.length > 0 ? (
        <ReliabilityWindowToggle
          window={reliability.window}
          onChange={reliability.onWindowChange}
        />
      ) : null}
      <SkyDataGrid<ScheduleSummary>
        data={schedules}
        columns={layout.columns}
        className="grid-pinned-last [&_table]:min-w-(--schedules-grid-floor)"
        {...LIST_GRID_PAGINATION}
        getRowId={(row) => row.id}
        loading={isLoading}
        emptyMessage="No schedules yet. A schedule decides when connections import and transformations run."
        testId="schedules-grid"
      />
      {/* D15: no caption under an empty list; D6: it says which columns are the viewer's. */}
      {schedules.length > 0 ? (
        <SkyText size={200} color="muted" block className="mt-2" testId="schedules-zone-caption">
          {`Next run and Last run are in ${zoneLabel(zone)}. Runs is in each schedule's own zone.`}
        </SkyText>
      ) : null}
      {/* Mounted only while open, so the schedule it was opened for is the one it pauses. */}
      {canWrite && pauseTarget ? (
        <PauseScheduleDialog schedule={pauseTarget} onClose={() => setPauseTarget(null)} />
      ) : null}
    </section>
  );
}
